/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../../ui/button";
import { useDeviceSummaryData } from "./hooks";
import { useDispatch, useSelector } from "react-redux";
import { isLoadingStatusSelector } from "../../../store/helpers/helperSelectors";
import { IDataGrid, IDataGridItem } from "../../../pages/Configuration/types";
import { Ioptions } from "../../Shared/Filter/types";
import { capitalizeWords, titleCase } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import AutocompleteSelect from "../../Shared/Dropdown/AutocompleteSelect";
import { useLabelsDisplay } from "../../Nodes/NodesFilter/hooks";
import TagLabel from "../../Shared/Tag/TagLabel";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import clsx from "clsx";
import { Check, ChevronDown, ChevronUp, Loader2, Pencil, X } from "lucide-react";
import { Input } from "../../ui/input";
import { Separator } from "../../ui/separator";
import { RadixSelectControl, RadixSelectOption } from "../../ui/radix-select";
import StatusIcon from "../../Shared/Status/StatusIcon";

interface IProps {
  rowData: IDataGrid;
  rowHeader: Array<any>;
}

const SummaryDisplayGrid: React.FC<IProps> = ({ rowHeader, rowData }) => {
  const MAX_LABELS = 10;
  const dispatch = useDispatch();
  const { selectedkey, setSelectedKey, handleUpdate, newValue, setNewValue, groupList, deviceDetail } = useDeviceSummaryData();
  const loadingStatus = useSelector(isLoadingStatusSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [isShowMore, setShowMore] = useState(false);

  const deviceLabelList = useLabelsDisplay();
  const labelList = (deviceLabelList?.data || []).map(x => x.trim()).filter(Boolean).map(x => ({
    title: x,
    value: x
  }));
  const handleChange = (e: any) => {
    setNewValue(e.target.value);
  };

  const [selectedLabels, setSelectedLabels] = useState<{ title: string; value: string }[]>([]);

  const availableLabelOptions = labelList.filter(
    (option) => !selectedLabels.some((selected) => selected.value === option.value)
  );
  useEffect(() => {
    if (deviceDetail?.labelNames) {
      const arr = deviceDetail.labelNames.split(",")
        .map(lbl => lbl.trim())
        .filter(Boolean)
        .map(lbl => ({ title: lbl, value: lbl }));

      setSelectedLabels(arr);
    }
  }, [deviceDetail?.labelNames]);

  const selectClassName = "w-[150px] text-[var(--link)] shadow-sm  mt-1 mr-1";

  const checkTextFieldType = (item: any) => {
    if (item === "OPERATING MODE") return true;
    else if (item === "PCI LOCK ENABLED") return true;
    else if (item === "NOTIFICATIONS") return true;
    else if (item === "RCP MODE") return true;
    else if (item === "DU MODE") return true;
    else if (item === "DEPLOYMENT TYPE") return true;
    else if (item === "LABEL") return true;
    else return false;
  };

  const handleLabelChange = (event: any, selected: { title: string; value: string }[]) => {
    event?.stopPropagation();

    if (selected.length > MAX_LABELS) {
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "error",
          message: `You can select maximum ${MAX_LABELS} labels only.`,
        })
      );
      return; // block extra selection
    }
    const isRemoving = selected.length < selectedLabels.length;

    // Allow removal always
    if (isRemoving) {
      setSelectedLabels(selected);
      const newValue = selected.map(x => x.value).join(",");
      setSelectedKey("LABEL");
      setNewValue(newValue);
      return;
    }

    const existing = new Set(selectedLabels.map((l) => l.value));

    const filtered = selected.filter((item) => !existing.has(item.value));

    // If no new unique item was selected, do nothing
    if (filtered.length === 0) return;

    const updated = [...selectedLabels, ...filtered];
    //  Update GUI instantly
    setSelectedLabels(updated);
    //  Prepare new Value for backend save
    const newValue = selected.map(x => x.value).join(",");

    setSelectedKey("LABEL");
    setNewValue(newValue);
  };

  const handleUpdateConfig = (item: string, value: any, newVal: any) => {
    if (value !== newVal) {
      handleUpdate(item, value, newVal);
    } else {
      setNewValue("");
      setSelectedKey("");
    }
  };
  const canEditItem = (itemKey: string): boolean => {
    switch (itemKey) {
      case "LABEL":
        return !!permissions?.hasOpCodeGroup("manage_label_management");
      default:
        return !!permissions?.hasOpCodeGroup("manage_topology");
    }
  };
  const handleSelectFieldType = (item: any, newVal: any, val: any) => {
    const currentValue = newVal ? newVal : val || "";

    const handleValueChange = (value: string) => {
      setNewValue(value);
    };

    switch (item) {
      case "LABEL":
        return (
           <AutocompleteSelect
            name="selectedLabels"
            value={selectedLabels}
            multiple={true}
            id="selectLabel"
            options={availableLabelOptions}
            label=""
            margin={"none"}
            size={"small"}
            onChange={handleLabelChange}
            placeholder="Select the Label"
            sx={{ mt: 1, mr: 1, '& .MuiInputBase-root': { minHeight: '16px', py: '1px' } }}
          />
        );
      case "OPERATING MODE": {
        const options: RadixSelectOption[] = [
          { value: "Maintenance", label: "Maintenance" },
          { value: "InService", label: "InService" },
        ];
        return (
          <RadixSelectControl
            options={options}
            value={currentValue}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select mode"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      case "NOTIFICATIONS": {
        const options: RadixSelectOption[] = [
          { value: "System Default", label: "System Default" },
          ...groupList
            .filter((g: Ioptions) => g.value !== "NONE")
            .map((g: Ioptions) => ({ value: g.value, label: g.text })),
        ];
        return (
          <RadixSelectControl
            options={options}
            value={currentValue || "System Default"}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select notification"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      case "DEPLOYMENT TYPE": {
        let options: RadixSelectOption[] = [];
        switch (deviceDetail.nodeType) {
          case "SN":
            options = [
              { value: "Regular devices", label: "Regular devices" },
              { value: "Centralized devices", label: "Centralized devices" },
              { value: "Devices feeding DAS systems", label: "Devices Feeding DAS System" },
            ];
            break;
          case "vCU":
          case "CU":
            options = [{ value: "Regular devices", label: "Regular devices" }];
            break;
          default:
            options = [];
        }
        return (
          <RadixSelectControl
            options={options}
            value={currentValue}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select deployment"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      case "PCI LOCK ENABLED": {
        const options: RadixSelectOption[] = [
          { value: "true", label: "True" },
          { value: "false", label: "False" },
        ];
        return (
          <RadixSelectControl
            options={options}
            value={currentValue || "false"}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      case "RCP MODE": {
        const options: RadixSelectOption[] = [
          { value: "Primary", label: "Primary" },
          { value: "Secondary", label: "Secondary" },
        ];
        return (
          <RadixSelectControl
            options={options}
            value={currentValue}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select RCP mode"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      case "DU MODE": {
        const options: RadixSelectOption[] = [
          { value: "Owner", label: "Owner" },
          { value: "Tenant-0", label: "Tenant-0" },
          { value: "Tenant-1", label: "Tenant-1" },
          { value: "Tenant-2", label: "Tenant-2" },
        ];
        return (
          <RadixSelectControl
            options={options}
            value={currentValue}
            onValueChange={handleValueChange}
            className={selectClassName}
            placeholder="Select DU mode"
            disabled={loadingStatus[selectedkey]}
          />
        );
      }
      default:
        return (
          <Input
            className="w-[150px] max-w-[200px]"
            onChange={handleChange}
            value={currentValue}
          />
        );
    }
  };

  const itemLabel = (item: IDataGridItem) => {
    let labelStr: string = "";
    if (item.key === "NOTIFICATIONS") {
      const groupvalue = rowData.settings.filter((item: IDataGridItem) => item.key === "NOTIFICATIONS")[0]?.value || "";
      labelStr = groupList.filter((item: Ioptions) => item.value === groupvalue)[0]?.text || "System Default";
    } else if (item.key === "PCI LOCK ENABLED") {
      labelStr = titleCase(item.value);
    } else if (item.key === "DEPLOYMENT TYPE") {
      labelStr = titleCase(item?.value);
    }
    else {
      labelStr = item.value;
    }
    if (item.key === "CONFIGURATION STATUS") {
      return labelStr?.trim().toLowerCase() === "na" ? "NA" : capitalizeWords(labelStr);
    }
    if (item.key === "CLUSTER ID") {
      return capitalizeWords(labelStr);
    }
    return labelStr;
  };

  const SectionDivider = () => (
    <div className="my-8 h-px w-full rounded-full bg-gradient-to-r from-transparent via-border to-transparent" />
  );

  return (
    <>
      <div
        className={clsx("px-6 pt-2 pb-4", "border border-border rounded-lg bg-card shadow-sm",
          isShowMore ? "max-h-[600px] overflow-y-auto" : "overflow-hidden")}>
        {Object.entries(rowData).map(([keyItem, items], index) => {
          if ((!isShowMore && index > 1) || items.length < 1) return "";
          return (
            <React.Fragment key={keyItem}>
              <section>
                <h2 className="text-sm font-semibold mb-0 text-foreground">
                  {titleCase(keyItem)}
                </h2>
              </section>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-[2px]">
                {items.map((item: IDataGridItem) => (
                  <div key={item.key}>

                    {/* label */}
                    <p className="text-sm text-muted-foreground leading-none mb-0">
                      {item.key.toUpperCase() === "MANAGEMENT IP" ? "Management IP" : item.key.toUpperCase() === "KPI SYNC STATUS" ? "KPI Sync Status" : titleCase(item.key)}
                    </p>

                    <div className="group flex items-center w-full mt-0">

                      {/* ---------------- VIEW MODE ---------------- */}

                      {item?.key !== selectedkey ? (
                        item.key === "LABEL" ? (
                          <TagLabel
                            labels={item.value ? item.value.split(",") : []}
                            maxVisible={2}
                          />
                        ) : item.key === "KPI SYNC STATUS" ? (
                          <div className="flex items-center mt-[4px] py-1">
                            <StatusIcon status={item.value} isLabel={false} />
                          </div>
                        ) : (
                          <p
                            title={item.value}
                            className={clsx(
                              "text-sm leading-tight text-foreground truncate w-full rounded-md transition-colors mt-[4px] py-1",
                              canEditItem(item.key) && item?.editIcon && "group-hover:bg-muted"
                            )}
                          >
                            {itemLabel(item) || "—"}
                          </p>
                        )
                      ) : checkTextFieldType(item?.key) ? (
                        handleSelectFieldType(item?.key, newValue, item?.value)
                      ) : (
                        <Input
                          disabled={loadingStatus[selectedkey]}
                          className="w-[150px] max-w-[200px] mt-1"
                          onChange={handleChange}
                          value={newValue}
                        />
                      )}

                      {/* EDIT BUTTON */}

                      {canEditItem(item.key) &&
                        item?.editIcon &&
                        item.key !== selectedkey &&
                        !loadingStatus[selectedkey] && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 transition-opacity
  h-7 w-7 bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent"
                            onClick={() => {
                              setNewValue(item?.value);
                              setSelectedKey(item?.key);
                            }}
                          >
                            <Pencil
                              className="h-4 w-4 text-foreground"
                              fill="currentColor"
                              strokeWidth={1.25}
                            />
                          </Button>
                        )}

                      {/* SAVE / CANCEL */}

                      {item.key === selectedkey && (
                        <div className="flex items-center">

                          {loadingStatus[selectedkey] ? (
                            <Loader2 className="h-4 w-4 animate-spin ml-2" />
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent"
                                onClick={() => setSelectedKey("")}
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent"
                                onClick={() =>
                                  handleUpdateConfig(
                                    item?.key,
                                    item?.value,
                                    newValue
                                  )
                                }
                              >
                                <Check className="h-4 w-4 text-success" />
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {index < Object.entries(rowData).length - 1 && <SectionDivider />}
            </React.Fragment>
          );
        })}
        <div className="mt-6 w-full">
          <Button
            variant="ghost"
            className="w-full flex justify-center items-center text-foreground
            bg-transparent hover:bg-muted
            transition-colors duration-200"
            aria-label={isShowMore ? "see less" : "see more"}
            onClick={() => setShowMore(!isShowMore)}
          >
            {isShowMore ? (
              <>
                <ChevronUp />
                <span className="text-xs">See less</span>
              </>
            ) : (
              <>
                <span className="text-xs">See more</span>
                <ChevronDown/>
              </>
            )}
          </Button>
        </div>
      </div >
    </>
  );
};

export default SummaryDisplayGrid;

